// UI theming via "Texture Packs" — a Storybook-style component explorer. A pack is a set of CSS
// BLOCKS, one per themeable component (global / duelfield / cardinfo / login / custom), each a CSS
// string the player edits directly (the editor prefills it with that component's variables at their
// current values, grouped by explanatory comments — see themeComponents.ts). The blocks are
// concatenated + injected into one <style>. Image bytes live LOCALLY in IndexedDB, never on servers.
//
// SECURITY (the trust boundary): imported packs are UNTRUSTED. On apply we strip @import and drop
// every external url() — only data:/blob: and our resolved url(assets/<name>) survive (built-ins are
// trusted). Blocks an imported pack from beaconing the importer's IP or exfiltrating typed values.

import { zipSync, unzipSync, strToU8, strFromU8 } from "fflate";
import pocField from "../../assets/poc-field.jpg"; // Power-of-Chaos marble playmat, shipped with Legacy
import { THEME_COMPONENTS, componentById, blockText } from "../theme/themeComponents.ts";

export interface ModPack {
  id: string;
  name: string;
  builtin?: boolean;
  /** componentId → that component's CSS text. Only components the player has touched are stored;
   *  untouched ones fall through to the app defaults (the editor still shows their prefilled values). */
  blocks: Record<string, string>;
  /** image file names in IndexedDB (key `${id}:${name}`), referenced from CSS as url(assets/<name>). */
  assets?: string[];
}

// Bundled images that resolve for ANY pack (so forking a built-in keeps its art without copying bytes).
const BUILTIN_ASSETS: Record<string, string> = { "poc-field.jpg": pocField };

// injection order: global (palette) first so component blocks can reference the resolved colours.
const BLOCK_ORDER = [...THEME_COMPONENTS.map((c) => c.id), "custom"];

// ── built-in themes (shipped, trusted) ───────────────────────────────────────────
const DEFAULT_THEME: ModPack = { id: "default", name: "Cyber", builtin: true, blocks: {} };

// Legacy Power-of-Chaos look, authored as the same per-component blocks a user edits.
const POC_THEME: ModPack = {
  id: "poc",
  name: "Legacy",
  builtin: true,
  blocks: {
    global: `/* ════ Global / Base ════
   Legacy — the warm Power-of-Chaos look: stone + gold palette, serif fonts, no neon. */

/* ── Palette ── */
:root {
  --color-void: #1c140c;       /* page background    */
  --color-void-2: #241a10;     /* deeper background  */
  --color-surface: #33271a;    /* panels             */
  --color-surface-2: #42321f;  /* raised panels      */
  --color-cyan: #e9c977;       /* accent (gold, not neon) */
  --color-cyan-dim: #a07f3e;
  --color-magenta: #c4502f;
  --color-lime: #cdb568;
  --color-ink: #f4ead2;        /* text               */
  --color-ink-dim: #bcaa86;
  --color-gold: #ffd86b;
}

/* ── Fonts ── */
:root {
  --font-display: 'Georgia', 'Times New Roman', serif;
  --font-body: 'Georgia', 'Times New Roman', serif;
}

/* ── Look (0 = off) ── */
:root {
  --fx-glow: 0;        /* neon glow off */
  --fx-grid: 0;        /* hex grid off  */
  --chamfer-scale: 0;  /* square corners */
}`,
    duelfield: `/* ════ Duel Field ════
   Legacy — the Power-of-Chaos marble playmat, with our zones dragged onto its printed slots.
   Each side has its own numbers; drag any zone on the board (Field helper) to fine-tune. */

/* ── Backgrounds & cards ── */
:root {
  --df-bg-field: radial-gradient(130% 100% at 50% 30%, #4a3622 0%, #271b0f 55%, #120c06 100%);  /* full duel background */
  --df-field: center / contain no-repeat url(assets/poc-field.jpg);  /* the marble playmat */
  --df-zone-dashed: 1;  /* dashed empty slots so the printed zones show through */
  --card-scale: 1;
}

/* ── Your side ── */
:root {
  --df-z-me-mzone-x: 40px;   --df-z-me-mzone-y: 28px;   --df-z-me-mzone-gap: 4px;
  --df-z-me-szone-x: 39px;   --df-z-me-szone-y: 59px;   --df-z-me-szone-gap: 4px;
  --df-z-me-field-x: 25px;   --df-z-me-field-y: 43px;
  --df-z-me-extra-x: 25px;   --df-z-me-extra-y: 55px;
  --df-z-me-gy-x: 57px;      --df-z-me-gy-y: 42px;
  --df-z-me-deck-x: 53px;    --df-z-me-deck-y: 55px;
  --df-z-me-banish-x: -36px; --df-z-me-banish-y: -123px; --df-z-me-banish-rot: 90deg;
  --df-z-me-hand-x: 22px;    --df-z-me-hand-y: 72px;    --df-z-me-hand-size: 1.2;
}

/* ── Opponent side ── */
:root {
  --df-z-opp-mzone-x: 38px;   --df-z-opp-mzone-y: 17px;  --df-z-opp-mzone-gap: 4px;
  --df-z-opp-szone-x: 46px;   --df-z-opp-szone-y: -11px; --df-z-opp-szone-gap: 4px;
  --df-z-opp-field-x: 57px;   --df-z-opp-field-y: -12px;
  --df-z-opp-extra-x: 54px;   --df-z-opp-extra-y: -6px;
  --df-z-opp-gy-x: 32px;      --df-z-opp-gy-y: -5px;
  --df-z-opp-deck-x: 30px;    --df-z-opp-deck-y: -8px;
  --df-z-opp-banish-x: -708px; --df-z-opp-banish-y: 146px; --df-z-opp-banish-rot: 90deg;
  --df-z-opp-hand-x: 20px;    --df-z-opp-hand-y: -87px;  --df-z-opp-hand-size: 1.5;
}`,
  },
};
const BUILTINS: ModPack[] = [DEFAULT_THEME, POC_THEME];

// ── persisted reactive state ─────────────────────────────────────────────────────
const KEY = "df.theme";
interface Persisted { activeId?: string; installed?: ModPack[]; }
function load(): Persisted {
  try { return JSON.parse(localStorage.getItem(KEY) ?? "{}"); } catch { return {}; }
}
// ponytail: migrate older formats — a single `css` string, or the pre-CSS `tokens` map — into a
// `custom` block. Old slot images are dropped (those formats were unreleased WIP).
function migrate(p: ModPack & { css?: string; tokens?: Record<string, string> }): ModPack {
  if (p.blocks) return p;
  if (typeof p.css === "string") return { id: p.id, name: p.name, blocks: { custom: p.css }, assets: p.assets };
  const body = Object.entries(p.tokens ?? {}).map(([k, v]) => `  ${k}: ${v};`).join("\n");
  return { id: p.id, name: p.name, blocks: body ? { custom: `:root {\n${body}\n}` } : {}, assets: p.assets };
}
const init = load();

export const themeState = $state({
  activeId: init.activeId ?? "default",
  installed: (init.installed ?? []).map(migrate) as ModPack[],
  /** true when the active pack supplies a real DUEL backdrop (--df-bg-field) → App skips CyberBackground
   *  behind duels. */
  hasDuelBg: false,
  /** true when it supplies a real MENU/LOGIN backdrop (--df-bg-menu) → App skips CyberBackground there.
   *  Two independent backgrounds so duels and menus can differ (or match). */
  hasMenuBg: false,
});

/** Which component the editor is focused on (drives the sidebar's prefilled CSS AND the isolation
 *  render in ModEditor). `fieldHelper` toggles the drag-to-edit overlay on the duel field. */
export const editorSel = $state({ id: "duelfield", fieldHelper: true });

/** Read a single CSS var's value out of a component's current block (for the drag helper). */
export function blockVar(id: string, key: string): string {
  const m = displayBlock(id).match(new RegExp(`${key}\\s*:\\s*([^;}]+)`));
  return (m?.[1] ?? "").trim();
}
/** Set a single CSS var in a component's block (insert if absent) — the drag helper writes through this
 *  so the CSS editor + board update together. */
export function setBlockVar(id: string, key: string, value: string): void {
  let css = displayBlock(id);
  const re = new RegExp(`(${key}\\s*:\\s*)[^;}]+`);
  css = re.test(css) ? css.replace(re, `$1${value}`) : css.trimEnd() + `\n:root { ${key}: ${value}; }\n`;
  setBlock(id, css);
}

export function allThemes(): ModPack[] { return [...BUILTINS, ...themeState.installed]; }
const activePack = (): ModPack => allThemes().find((x) => x.id === themeState.activeId) ?? DEFAULT_THEME;

/** The CSS text shown for a component: the pack's stored block, else the prefilled defaults. */
export function displayBlock(id: string): string {
  const stored = activePack().blocks[id];
  if (stored !== undefined) return stored;
  const c = componentById(id);
  return c ? blockText(c) : "";
}

function persist(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ activeId: themeState.activeId, installed: themeState.installed }));
  } catch { /* private mode — won't persist */ }
}

// ── IndexedDB asset store (blobs keyed by string; mirrors audio's tiny idb helper) ──
const DB_NAME = "df.modassets";
const STORE = "assets";
let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  return (dbp ??= new Promise((res, rej) => {
    const r = indexedDB.open(DB_NAME, 1);
    r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE); };
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  }));
}
function putAsset(id: string, blob: Blob): Promise<void> {
  return db().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE, "readwrite"); t.objectStore(STORE).put(blob, id);
    t.oncomplete = () => res(); t.onerror = () => rej(t.error);
  }));
}
function getAsset(id: string): Promise<Blob | null> {
  return db().then((d) => new Promise((res, rej) => {
    const r = d.transaction(STORE, "readonly").objectStore(STORE).get(id);
    r.onsuccess = () => res((r.result as Blob) ?? null); r.onerror = () => rej(r.error);
  }));
}
function delAsset(id: string): Promise<void> {
  return db().then((d) => new Promise((res, rej) => {
    const t = d.transaction(STORE, "readwrite"); t.objectStore(STORE).delete(id);
    t.oncomplete = () => res(); t.onerror = () => rej(t.error);
  }));
}

// ── apply: sanitize + inject the pack's concatenated blocks into one <style> ──────
let objectUrls: string[] = [];
let styleEl: HTMLStyleElement | null = null;
function sheet(): HTMLStyleElement {
  if (!styleEl) { styleEl = document.createElement("style"); styleEl.id = "texture-pack"; document.head.appendChild(styleEl); }
  return styleEl;
}

const ASSET_REF = /^\.?\/?(?:assets\/)?/i; // strips "assets/", "./assets/", "/" → bare file name

async function assetUrls(pack: ModPack): Promise<Map<string, string>> {
  const m = new Map<string, string>();
  for (const name of pack.assets ?? []) {
    const blob = await getAsset(`${pack.id}:${name}`);
    if (blob) { const u = URL.createObjectURL(blob); objectUrls.push(u); m.set(name, u); }
  }
  for (const [name, url] of Object.entries(BUILTIN_ASSETS)) if (!m.has(name)) m.set(name, url);
  return m;
}

function sanitize(css: string, urls: Map<string, string>, trusted: boolean): string {
  let out = css.replace(/@import[^;]*;?/gi, "");
  out = out.replace(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi, (m, _q, raw: string) => {
    const ref = raw.trim();
    if (/^(data:|blob:)/i.test(ref)) return m;
    const url = urls.get(ref.replace(ASSET_REF, ""));
    if (url) return `url("${url}")`;
    return trusted ? m : "url()";
  });
  return out;
}

/** The pack's full stylesheet: stored blocks concatenated in injection order. */
function packCss(pack: ModPack): string {
  const ids = [...BLOCK_ORDER, ...Object.keys(pack.blocks).filter((k) => !BLOCK_ORDER.includes(k))];
  return ids.map((id) => pack.blocks[id]).filter(Boolean).join("\n\n");
}

/** Is a backdrop var set to a real value (not transparent/none/unset)? Drives skip-the-cyber-bg. */
function realBg(css: string, name: string): boolean {
  const m = css.match(new RegExp(`${name}\\s*:\\s*([^;]+)`));
  return !!m && !/^\s*(transparent|none|inherit|initial)\s*$/i.test(m[1] ?? "");
}

async function apply(pack: ModPack): Promise<void> {
  for (const u of objectUrls) URL.revokeObjectURL(u);
  objectUrls = [];
  const css = packCss(pack);
  const urls = await assetUrls(pack);
  sheet().textContent = sanitize(css, urls, !!pack.builtin);
  // A backdrop counts only when set to a REAL value — so prefilling it as `transparent` (the default)
  // never wrongly hides the animated cyber background.
  themeState.hasDuelBg = realBg(css, "--df-bg-field");
  themeState.hasMenuBg = realBg(css, "--df-bg-menu");
}

export function setActiveTheme(id: string): void {
  themeState.activeId = (allThemes().find((x) => x.id === id) ?? DEFAULT_THEME).id;
  persist();
  void apply(activePack());
}

// ── editing: built-ins are immutable, so the first edit clones the active pack into `user:custom` ──
function ensureUserPack(): ModPack {
  let pack = themeState.installed.find((p) => p.id === themeState.activeId);
  if (!pack) {
    const base = activePack();
    pack = { id: "user:custom", name: "My pack", blocks: { ...base.blocks }, assets: [...(base.assets ?? [])] };
    themeState.installed = [...themeState.installed.filter((p) => p.id !== "user:custom"), pack];
  }
  return pack;
}

/** Live-edit one component's CSS block. */
export function setBlock(id: string, css: string): void {
  const pack = ensureUserPack();
  pack.blocks = { ...pack.blocks, [id]: css };
  themeState.installed = [...themeState.installed];
  setActiveTheme(pack.id);
}

/** Store an uploaded image on the active pack; returns its name (caller inserts url(assets/<name>)). */
export async function addAsset(file: File): Promise<string> {
  const pack = ensureUserPack();
  const name = uniqueName(pack, (file.name || "image").replace(/[^\w.-]+/g, "_"));
  await putAsset(`${pack.id}:${name}`, file);
  pack.assets = [...(pack.assets ?? []), name];
  themeState.installed = [...themeState.installed];
  setActiveTheme(pack.id);
  return name;
}
/** The active pack's uploaded image names (for the editor's deletable asset list). */
export function activeAssets(): string[] { return [...(activePack().assets ?? [])]; }

/** A fresh object URL for one uploaded image of the active pack (caller revokes). Editor thumbnails. */
export async function assetObjectUrl(name: string): Promise<string | null> {
  const blob = await getAsset(`${activePack().id}:${name}`);
  return blob ? URL.createObjectURL(blob) : null;
}

/** Delete one uploaded image from the active pack (the editor's asset list × button). */
export async function removeAsset(name: string): Promise<void> {
  const pack = themeState.installed.find((p) => p.id === themeState.activeId);
  if (!pack?.assets?.includes(name)) return;
  await delAsset(`${pack.id}:${name}`);
  pack.assets = pack.assets.filter((a) => a !== name);
  themeState.installed = [...themeState.installed];
  setActiveTheme(pack.id);
}

function uniqueName(pack: ModPack, name: string): string {
  const have = new Set(pack.assets ?? []);
  if (!have.has(name)) return name;
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 2; ; i++) { const n = `${stem}-${i}${ext}`; if (!have.has(n)) return n; }
}

export async function removeModpack(id: string): Promise<void> {
  const pack = themeState.installed.find((p) => p.id === id);
  if (!pack) return;
  for (const name of pack.assets ?? []) await delAsset(`${pack.id}:${name}`);
  themeState.installed = themeState.installed.filter((p) => p.id !== id);
  if (themeState.activeId === id) setActiveTheme("default");
  else persist();
}

// ── import / export as a .zip { <component>.css …, assets/*, index.html } ──────────
const MAX_ASSETS = 12;
const MAX_ASSET_BYTES = 12_000_000;
const MIME: Record<string, string> = {
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
  svg: "image/svg+xml", avif: "image/avif", woff: "font/woff", woff2: "font/woff2", ttf: "font/ttf",
};
const mimeOf = (name: string): string => MIME[name.split(".").pop()?.toLowerCase() ?? ""] ?? "application/octet-stream";
const referencedNames = (css: string): string[] =>
  [...css.matchAll(/url\(\s*['"]?([^'")]*)['"]?\s*\)/gi)]
    .map((m) => (m[1] ?? "").trim()).filter((r) => !/^(data:|blob:)/i.test(r)).map((r) => r.replace(ASSET_REF, ""))
    .filter((n, i, a) => n && a.indexOf(n) === i);

async function assetBytes(pack: ModPack, name: string): Promise<Uint8Array | null> {
  const blob = await getAsset(`${pack.id}:${name}`);
  if (blob) return new Uint8Array(await blob.arrayBuffer());
  const url = BUILTIN_ASSETS[name];
  if (url) return new Uint8Array(await (await fetch(url)).arrayBuffer());
  return null;
}

const esc = (s: string): string => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
function referenceHtml(pack: ModPack): string {
  const blocks = Object.entries(pack.blocks).map(([id, css]) => `<h2>${esc(componentById(id)?.label ?? id)} <small>(${esc(id)}.css)</small></h2><pre>${esc(css)}</pre>`).join("\n");
  const ref = THEME_COMPONENTS.map((c) => `<h3>${esc(c.label)}</h3><pre>${esc(blockText(c))}</pre>`).join("\n");
  return `<!doctype html><html lang=en><meta charset=utf8><meta name=viewport content="width=device-width,initial-scale=1">
<title>${esc(pack.name)} — YGO Evolution Texture Pack</title>
<style>body{font:14px/1.6 system-ui,sans-serif;max-width:64rem;margin:auto;padding:2rem;background:#0a0f1e;color:#cfe6f5}
h1,h2,h3{color:#19e0ff}small{color:#8fb6d6;font-weight:400}code{color:#9cff57}pre{background:#05070f;padding:1rem;border-radius:8px;overflow:auto;color:#bfe;border:1px solid #13213f}</style>
<h1>${esc(pack.name)}</h1>
<p>A YGO Evolution <b>Texture Pack</b>. Each <code>&lt;component&gt;.css</code> file themes one part of the UI; put
images in <code>assets/</code> and reference them as <code>url(assets/your-image.png)</code>. Edit here and re-zip,
or paste a block into the in-app editor (<b>Settings → Texture Pack</b>).</p>
${blocks ? "<h2>This pack</h2>" + blocks : ""}
<h1>Reference — every component & variable</h1>
${ref}`;
}

function download(data: Uint8Array, name: string, type: string): void {
  const u = URL.createObjectURL(new Blob([data as BlobPart], { type }));
  const a = document.createElement("a"); a.href = u; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
}

export async function exportModpack(id: string): Promise<void> {
  const t = allThemes().find((x) => x.id === id);
  if (!t) return;
  const files: Record<string, Uint8Array> = {};
  for (const [bid, css] of Object.entries(t.blocks)) if (css.trim()) files[`${bid}.css`] = strToU8(css);
  for (const name of referencedNames(packCss(t))) {
    const bytes = await assetBytes(t, name);
    if (bytes) files[`assets/${name}`] = bytes;
  }
  files["index.html"] = strToU8(referenceHtml(t));
  download(zipSync(files, { level: 0 }), (t.name || "texture-pack").replace(/[^\w.-]+/g, "_") + ".zip", "application/zip");
}

export async function importModpack(file: File): Promise<void> {
  let entries: Record<string, Uint8Array>;
  try { entries = unzipSync(new Uint8Array(await file.arrayBuffer())); }
  catch { throw new Error("That isn't a valid .zip texture pack."); }

  const blocks: Record<string, string> = {};
  for (const [k, bytes] of Object.entries(entries)) {
    if (!/(^|\/)[\w-]+\.css$/i.test(k) || k.endsWith("/")) continue;
    const id = k.split("/").pop()!.replace(/\.css$/i, "");
    // a lone legacy theme.css → the custom block
    blocks[id === "theme" ? "custom" : id] = strFromU8(bytes);
  }
  if (!Object.keys(blocks).length) throw new Error("Texture pack has no .css files.");

  const name = (file.name.replace(/\.zip$/i, "").replace(/[_-]+/g, " ").trim() || "Imported pack").slice(0, 40);
  const idBase = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const id = "user:" + (idBase || "pack");
  const assetEntries = Object.entries(entries).filter(([k]) => /(^|\/)assets\/[^/]+$/i.test(k) && !k.endsWith("/"));
  if (assetEntries.length > MAX_ASSETS) throw new Error("Texture pack has too many images.");
  const assets: string[] = [];
  for (const [k, bytes] of assetEntries) {
    const base = k.split("/").pop()!; // basename only — keys, never the filesystem, so no traversal risk
    if (!base || bytes.length > MAX_ASSET_BYTES) continue;
    await putAsset(`${id}:${base}`, new Blob([bytes as BlobPart], { type: mimeOf(base) }));
    assets.push(base);
  }
  themeState.installed = [...themeState.installed.filter((p) => p.id !== id), { id, name, blocks, assets }];
  setActiveTheme(id);
}

// Apply the persisted pack as soon as the module loads (before screens mount → no flash).
void apply(activePack());

// ── dev self-check (the security-relevant bit: untrusted CSS sanitising) ──────────
if (import.meta.env?.DEV) {
  const urls = new Map([["playmat.jpg", "blob:resolved"]]);
  const out = sanitize(
    `@import url(//evil.css);
     :root{--a:url(assets/playmat.jpg);--b:url(https://evil/x.png);--c:url(data:image/png;base64,AA);}`,
    urls, false,
  );
  console.assert(!/@import/i.test(out), "theme: @import must be stripped");
  console.assert(out.includes('url("blob:resolved")'), "theme: known asset must resolve");
  console.assert(!/evil/.test(out), "theme: external url() must be dropped");
  console.assert(/data:image\/png/.test(out), "theme: data: url() must survive");
}
